import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import logoFull from '@/assets/fluxo-logo-full.png';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Bot,
  LayoutDashboard,
  Users,
  MessageSquare,
  Ticket,
  FileText,
  Sparkles,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Snowflake,
  Workflow,
  Calculator,
  Building2,
  Cpu,
  Radio,
  Package,
  Bell,
  Search,
  Settings,
  Hammer,
  BarChart3,
  Network
} from 'lucide-react';

const PRODUCT_NAME = 'Fluxo Co';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const token = localStorage.getItem('fluxoco_token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenants`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const sorted = response.data.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setTenants(sorted);
      } catch (error) {
        console.error('Error sidebar:', error);
      }
    };
    fetchTenants();
  }, []);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/clients', icon: Users, label: 'Tenants' },
    { path: '/master-ai', icon: Cpu, label: 'Master AI', highlight: true },
    { path: '/bots', icon: Bot, label: 'Bots' },
    { path: '/factory', icon: Network, label: 'Brain Factory' },
    { path: '/blueprint-studio', icon: Hammer, label: 'Blueprint Studio', highlight: true },
    { path: '/engine-structure', icon: Building2, label: 'Data Engine', highlight: true },
    { path: '/monitoring', icon: Radio, label: 'Control Tower' },
    { path: '/prospectos', icon: Calculator, label: 'Quotes' },
    { path: '/tickets', icon: Ticket, label: 'Tickets' },
    { path: '/reports', icon: BarChart3, label: 'Analytics' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 overflow-x-hidden font-inter">
      {/* Dynamic Glow background */}
      <div className="glow-bg" />
      
      {/* Mobile Drawer Trigger */}
      <div className="lg:hidden fixed bottom-6 right-6 z-50">
        <Button 
          size="icon" 
          className="w-14 h-14 rounded-full shadow-2xl bg-blue-600 hover:bg-blue-700"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X /> : <Menu />}
        </Button>
      </div>

      {/* Floating Sidebar Container */}
      <AnimatePresence mode="wait">
        {(sidebarOpen || window.innerWidth >= 1024) && (
          <motion.aside 
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 120 }}
            className={`fixed inset-y-0 left-0 z-40 w-72 m-6 rounded-[2.5rem] sidebar-midnight hidden lg:flex flex-col p-8 backdrop-blur-3xl`}
          >
            {/* Brand Section - Logo Only */}
            <div className="flex justify-center mb-8 px-2 group cursor-pointer" onClick={() => navigate('/dashboard')}>
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <img 
                  src={logoFull} 
                  alt="Fluxo Co" 
                  className="h-12 w-auto relative z-10 invert hue-rotate-180 brightness-200 group-hover:scale-110 transition-transform duration-500" 
                />
              </div>
            </div>

            {/* Main Navigation */}
            <nav className="flex-1 space-y-1 overflow-y-auto pr-2 custom-scrollbar">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => 
                    `sidebar-link-premium ${isActive ? 'active' : ''}`
                  }
                >
                  <div className="relative z-10 w-10 h-10 rounded-xl flex items-center justify-center bg-slate-900/40 border border-white/5 transition-all group-hover:rotate-6 group-hover:scale-110">
                    <item.icon className={`w-5 h-5 ${item.highlight ? 'text-blue-400' : 'text-slate-400'}`} />
                  </div>
                  <span className="flex-1 text-sm font-semibold">{item.label}</span>
                  {item.highlight && (
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
                  )}
                </NavLink>
              ))}

            </nav>

            {/* Bottom User Control Center - Compact */}
            <div className="mt-auto pt-2 border-t border-slate-800/50">
              <div className="group relative p-2.5 rounded-2xl bg-white/5 border border-white/5 hover:bg-emerald-500/5 hover:border-emerald-500/20 transition-all duration-500">
                <div className="flex items-center gap-2.5">
                  <Avatar className="h-8 w-8 border-2 border-emerald-500/20 group-hover:border-emerald-500/40 transition-colors">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-emerald-500/10 text-emerald-500 text-[10px] font-bold">{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white truncate leading-tight">{user?.name}</p>
                    <p className="text-[8px] text-emerald-500/60 font-black uppercase tracking-[0.1em] mt-0.5">Brain Factory</p>
                  </div>
                </div>
                
                {/* Compact Action Row */}
                <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/5">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                    onClick={() => navigate('/settings')}
                    title="Configuración"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                    onClick={handleLogout}
                    title="Cerrar Sesión"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Mobile Sidebar (Drawer) */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-40 lg:hidden" 
            />
            <motion.aside 
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className="fixed inset-y-0 left-0 w-80 bg-slate-950 z-50 p-6 flex flex-col lg:hidden border-r border-slate-800"
            >
              {/* Similar content for mobile sidebar... can be extracted to a component later */}
              <div className="flex items-center justify-between mb-8">
                 <div className="flex items-center gap-3">
                    <Workflow className="w-8 h-8 text-blue-500" />
                    <h1 className="text-lg font-bold font-outfit">{PRODUCT_NAME}</h1>
                 </div>
                 <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                   <X />
                 </Button>
              </div>
              {/* Navigation items repeated for mobile */}
              <nav className="flex-1 space-y-2 overflow-y-auto">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => 
                      `flex items-center gap-4 p-4 rounded-2xl ${isActive ? 'bg-blue-600 text-white' : 'text-slate-400'}`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className={`transition-all duration-500 ease-in-out lg:pl-[21rem] min-h-screen`}>
        <div className="max-w-[1600px] mx-auto p-4 lg:p-6">
          
          {/* Enhanced Header */}
          <header className={`flex items-center justify-between mb-8 pb-4 border-b border-slate-800/50 transition-all duration-300 ${scrolled ? 'sticky top-4 z-30 p-4 bg-slate-950/80 backdrop-blur-xl rounded-2xl border-slate-700/50' : ''}`}>
            <div>
               <h2 className="text-[10px] uppercase tracking-[0.4em] font-black text-emerald-500/60 mb-2">
                 Brain Factory
               </h2>
               <div className="flex items-center gap-2">
                 <div className="w-1.5 h-6 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                 <h1 className="text-2xl lg:text-3xl font-bold font-outfit text-white">
                   {navItems.find(i => i.path === location.pathname)?.label || 'Centrómetro'}
                 </h1>
               </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center group relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors z-10" />
                <input 
                  type="text" 
                  placeholder="Buscar en la maquinaria..." 
                  className="bg-slate-900/40 border border-white/10 rounded-2xl py-2.5 pl-10 pr-4 w-64 text-sm text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] focus:outline-none transition-all duration-300"
                />
              </div>

              <Button variant="ghost" size="icon" className="h-10 w-10 bg-slate-900/50 rounded-2xl relative text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all">
                <Bell className="w-5 h-5" />
                <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-emerald-500 rounded-full border-2 border-slate-950 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
              </Button>
            </div>
          </header>

          {/* Page Content with Page Transitions */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
